// Broker portal — concierge mode. Broker manages seller and buyer clients
// end-to-end on their behalf. Full seller journey + buyer search embedded
// within /broker — no redirects to /sell or /buy.
//
// Route: /broker
// Auth:  same BETA_SECRET
// Data:  sz_broker_clients (localStorage) — array of client objects

import { useEffect, useRef, useState } from "react";
import { useAuth } from "../components/AuthGate";
import MessageThread from "../components/MessageThread";
import BrokerWorkboard from "./BrokerWorkboard";
import BrokerRequestsPanel from "./BrokerRequestsPanel";
import { supabase } from "../lib/supabase";
import { openDisclosurePDF } from "../lib/disclosurePDF";
import { STAGES, STAGE_COUNT } from "../seller/stages";
import { SELLER_CSS } from "../seller/styles/seller";
import { BETA_SECRET, BETA_KEY, callAI, lookupProperty, normalizeAddressKey, track } from "../seller/lib/api";
import { getStageState, patchStage, blankJourney } from "../seller/lib/journey";
import { renderMD } from "../seller/lib/markdown";
import StageShell from "../seller/components/StageShell";
import Stepper from "../seller/components/Stepper";
import Logo from "../seller/components/Logo";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { DEMO_JOURNEY } from "../seller/lib/demoJourney";

// ─── Client storage ───────────────────────────────────────────────────────────
const CLIENTS_KEY = "sz_broker_clients";
const loadClients = () => { try { return JSON.parse(localStorage.getItem(CLIENTS_KEY) || "[]"); } catch { return []; } };
const saveClients = (arr) => { try { localStorage.setItem(CLIENTS_KEY, JSON.stringify(arr)); } catch {} };
const newId = () => crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36);

// ─── Notification storage ─────────────────────────────────────────────────────
const NOTIF_KEY = "sz_broker_notifications";
const loadNotifs  = () => { try { return JSON.parse(localStorage.getItem(NOTIF_KEY) || "[]"); } catch { return []; } };
const saveNotifs  = (arr) => { try { localStorage.setItem(NOTIF_KEY, JSON.stringify(arr.slice(0, 100))); } catch {} };
const mkNotif     = (fields) => ({ id: newId(), created_at: new Date().toISOString(), read: false, ...fields });

// ─── Sample clients ─────────────────────────────────────────────────────────
// Shown only when the broker has zero real clients (fresh sign-in, e.g. a
// broker trying the product for the first time) — a read-only preview of
// what a fully-worked seller/buyer client looks like, using the same
// pre-seeded demo journey the ?demo=1 seller flow uses. Never persisted to
// localStorage, never mixed into real client data.
const now = new Date().toISOString();
const SAMPLE_CLIENTS = [
  {
    id: "sample-seller", is_sample: true, type: "seller",
    name: DEMO_JOURNEY.verification.owner_of_record,
    email: "sarah.thornton@example.com", phone: "",
    address: DEMO_JOURNEY.address, property: DEMO_JOURNEY.property,
    currentStageIdx: DEMO_JOURNEY.currentStageIdx, stages: DEMO_JOURNEY.stages,
    verification: DEMO_JOURNEY.verification,
    created_at: now, updated_at: now,
  },
  {
    id: "sample-buyer", is_sample: true, type: "buyer",
    name: "Marcus Chen", email: "marcus.chen@example.com", phone: "",
    location: "Noe Valley, San Francisco, CA", price_min: 1500000, price_max: 2200000,
    beds_min: "3", address: null, property: null, currentStageIdx: 0, stages: {},
    created_at: now, updated_at: now,
  },
];

const STAGE_LABELS = {
  valuation:"Valuation", prep:"Prep & Repairs", staging:"Staging",
  photography:"Photography", listing:"Listing Copy", mls:"MLS",
  disclosures:"Disclosures", open_house:"Open House", showings:"Showings",
  offers:"Offers", escrow:"Escrow", closing:"Closing",
};

const updateClient = (clients, id, patch) =>
  clients.map((c) => c.id === id ? { ...c, ...patch, updated_at: new Date().toISOString() } : c);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtRelative = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};
const fmtPrice = (n) => n == null ? "" : `$${Math.round(n).toLocaleString()}`;

// ─── Design ───────────────────────────────────────────────────────────────────
const mono = "'DM Mono',monospace";
const serif = "'Playfair Display',serif";
const sans = "'Outfit','Inter',sans-serif";

const BROKER_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Outfit:wght@400;600;700&family=Playfair+Display:ital,wght@0,700;1,700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{margin:0;font-family:${sans};background:#f2f8f5;color:#1c2b25}
  @keyframes fadeIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{to{transform:rotate(360deg)}}
  /* override seller app's full-viewport assumption inside broker right panel */
  .br-seller-panel .s-app{min-height:0!important;flex:1;display:flex;flex-direction:column}
  .br-seller-panel .s-stage-wrap{flex:1}
  /* broker-scoped AAC input inherits seller styles */
  .br-seller-panel .s-aac-wrap{flex:1;min-width:240px;text-align:left}
  .br-seller-panel .s-intake{flex:1}
`;

// ─── Seller progress bar ──────────────────────────────────────────────────────
function SellerProgressBar({ client }) {
  const stages = client.stages || {};
  return (
    <div style={{ display: "flex", gap: 2, marginTop: 6 }}>
      {STAGES.map((s, i) => {
        const st = stages[s.id]?.status || "pending";
        const isCur = i === (client.currentStageIdx || 0);
        return (
          <div key={s.id} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: isCur ? "#2d7a62" : st === "done" ? "#7ecfb0" : "#d8ede6",
          }} title={s.short} />
        );
      })}
    </div>
  );
}

// ─── Client card (sidebar) ────────────────────────────────────────────────────
function ClientCard({ client, active, onClick }) {
  const isSeller = client.type === "seller";
  const accentColor = isSeller ? "#2d7a62" : "#3b6ea8";
  const bgColor = isSeller ? "#e4f7ef" : "#e4eef7";
  const done = isSeller
    ? STAGES.filter((s) => client.stages?.[s.id]?.status === "done").length
    : null;

  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", textAlign: "left", background: active ? "#fff" : "transparent",
        border: active ? "1.5px solid #d8ede6" : "1.5px solid transparent",
        borderRadius: 12, padding: "12px 14px", cursor: "pointer",
        transition: "all 0.15s", display: "block",
        boxShadow: active ? "0 2px 10px rgba(0,0,0,0.06)" : "none",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1c2b25", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {client.name || <span style={{ color: "#9bbfb2", fontStyle: "italic" }}>Unnamed</span>}
        </div>
        <span style={{ display: "flex", gap: 4, flexShrink: 0, marginLeft: 6 }}>
          {client.is_sample && (
            <span style={{ fontSize: 10, fontFamily: mono, background: "#fef4e8", color: "#c47c1a", padding: "2px 7px", borderRadius: 8, letterSpacing: "0.06em" }}>
              SAMPLE
            </span>
          )}
          <span style={{ fontSize: 10, fontFamily: mono, background: bgColor, color: accentColor, padding: "2px 7px", borderRadius: 8, letterSpacing: "0.06em" }}>
            {isSeller ? "SELLER" : "BUYER"}
          </span>
        </span>
      </div>
      {isSeller && client.address && (
        <div style={{ fontSize: 11, color: "#7aad98", fontFamily: mono, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {client.address}
        </div>
      )}
      {!isSeller && client.location && (
        <div style={{ fontSize: 11, color: "#7aad98", fontFamily: mono, marginBottom: 4 }}>
          {client.location}{client.price_max ? ` · up to ${fmtPrice(client.price_max)}` : ""}
        </div>
      )}
      {isSeller && <SellerProgressBar client={client} />}
      {isSeller && done != null && (
        <div style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2", marginTop: 5, display: "flex", justifyContent: "space-between" }}>
          <span>{done}/{STAGE_COUNT} steps · {fmtRelative(client.updated_at)}</span>
{(() => {
            const closeDate = client.timeline?.close_date || computeTimeline(client.stages || {}).estimatedCloseDate;
            const d = Math.ceil((new Date(closeDate + "T12:00:00") - new Date()) / 86400000);
            const color = d <= 7 ? "#c0354a" : d <= 30 ? "#c47c1a" : "#2d7a62";
            return <span style={{ color, fontWeight: 600 }}>{d > 0 ? `${d}d` : d === 0 ? "today" : "closed"}</span>;
          })()}
        </div>
      )}
      {!isSeller && (
        <div style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2", marginTop: 5 }}>
          {fmtRelative(client.created_at)}
        </div>
      )}
    </button>
  );
}

// ─── Add client modal ─────────────────────────────────────────────────────────
function AddClientModal({ onAdd, onClose, brokerId, brokerName, prefill }) {
  const [type, setType] = useState(prefill?.type || "seller");
  const [form, setForm] = useState({
    name: prefill?.name || "", email: prefill?.email || "", phone: prefill?.phone || "",
    location: "", price_min: "", price_max: "", beds_min: "",
  });
  const [inviteStatus, setInviteStatus] = useState(null); // null | "sending" | "sent" | "no_email" | "error"
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const client = {
      id: newId(), type,
      name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
      created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      address: null, property: null, currentStageIdx: 0, stages: {},
      location: form.location.trim(),
      price_min: form.price_min ? Number(form.price_min) : null,
      price_max: form.price_max ? Number(form.price_max) : null,
      beds_min: form.beds_min || "",
    };
    onAdd(client);

    if (!form.email.trim()) { setInviteStatus("no_email"); return; }

    setInviteStatus("sending");
    try {
      const res = await fetch("/api/admin/broker-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({ clientName: form.name.trim(), clientEmail: form.email.trim(), brokerName, role: type, brokerId: brokerId || null }),
      });
      const data = await res.json();
      setInviteStatus(data.ok ? "sent" : "error");
      if (data.ok) setTimeout(onClose, 1200);
    } catch {
      setInviteStatus("error");
    }
  };

  const inputStyle = {
    width: "100%", background: "#f7faf8", border: "1.5px solid #d8ede6",
    color: "#1c2b25", borderRadius: 10, padding: "10px 13px", fontSize: 14, fontFamily: sans,
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,25,20,0.55)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", border: "1px solid #d8ede6", borderRadius: 20, padding: 28, width: "100%", maxWidth: 440, animation: "fadeIn 0.22s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        <div style={{ fontFamily: serif, fontSize: 22, fontWeight: 700, color: "#1c2b25", marginBottom: 20 }}>Add client</div>

        {/* Type toggle */}
        <div style={{ display: "flex", background: "#f2f8f5", borderRadius: 10, padding: 3, marginBottom: 20 }}>
          {["seller", "buyer"].map((t) => (
            <button key={t} type="button" onClick={() => setType(t)} style={{
              flex: 1, background: type === t ? "#fff" : "transparent", border: "none",
              borderRadius: 8, padding: "9px", fontSize: 13, fontWeight: type === t ? 700 : 400,
              color: type === t ? "#1c2b25" : "#9bbfb2", cursor: "pointer", fontFamily: sans,
              boxShadow: type === t ? "0 1px 4px rgba(0,0,0,0.08)" : "none", transition: "all 0.15s",
            }}>
              {t === "seller" ? "Seller client" : "Buyer client"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, fontFamily: mono, color: "#5a8a78", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>Client name *</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" style={inputStyle} autoFocus />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontFamily: mono, color: "#5a8a78", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>Email</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" style={inputStyle} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontFamily: mono, color: "#5a8a78", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>Phone</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(415) 555-0100" style={inputStyle} />
            </div>
          </div>

          {type === "buyer" && (
            <>
              <div>
                <label style={{ display: "block", fontSize: 11, fontFamily: mono, color: "#5a8a78", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>Search area</label>
                <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="San Francisco, CA" style={inputStyle} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                {[["price_min", "Min $", "800000"], ["price_max", "Max $", "1500000"], ["beds_min", "Min beds", "2"]].map(([k, label, ph]) => (
                  <div key={k}>
                    <label style={{ display: "block", fontSize: 11, fontFamily: mono, color: "#5a8a78", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5 }}>{label}</label>
                    <input type="number" value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} style={inputStyle} />
                  </div>
                ))}
              </div>
            </>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button type="submit" disabled={inviteStatus === "sending" || inviteStatus === "sent"} style={{ flex: 1, background: inviteStatus === "sent" ? "#1c5448" : "#2d7a62", color: "#fff", border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: sans, transition: "background 0.15s" }}>
              {inviteStatus === "sending" ? "Sending invite…" : inviteStatus === "sent" ? "✓ Invite sent!" : `Add ${type === "seller" ? "seller" : "buyer"} & invite →`}
            </button>
            <button type="button" onClick={onClose} style={{ background: "#f2f8f5", color: "#5a8a78", border: "1px solid #d8ede6", borderRadius: 10, padding: "13px 16px", fontSize: 13, cursor: "pointer", fontFamily: mono }}>
              Cancel
            </button>
          </div>
          {inviteStatus === "no_email" && (
            <p style={{ fontSize: 11, fontFamily: mono, color: "#9bbfb2", marginTop: 8, letterSpacing: "0.04em" }}>Client added. No email entered — share the invite link manually from the Messages tab.</p>
          )}
          {inviteStatus === "error" && (
            <p style={{ fontSize: 11, fontFamily: mono, color: "#c0354a", marginTop: 8, letterSpacing: "0.04em" }}>Client added but invite email failed — copy the link from the Messages tab instead.</p>
          )}
        </form>
      </div>
    </div>
  );
}

// ─── Seller: address entry — uses real seller intake CSS classes ──────────────
function SellerAddressEntry({ client, onAddressSet }) {
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [manual, setManual] = useState({ beds: "", baths: "", sqft: "", year_built: "" });
  const setM = (k, v) => setManual((m) => ({ ...m, [k]: v }));

  const submitManual = (e) => {
    e?.preventDefault();
    if (!address.trim()) { setErr("Enter the property address first."); return; }
    const property = {
      property: [{ building: { rooms: { beds: Number(manual.beds) || null, bathstotal: Number(manual.baths) || null }, size: { bldgsize: Number(manual.sqft) || null }, yearbuilt: Number(manual.year_built) || null }, lot: {} }],
    };
    onAddressSet(address.trim(), property);
  };

  const submit = async (e) => {
    e?.preventDefault();
    const a = address.trim();
    if (a.length < 8) { setErr("Enter a full address — street, city, state."); return; }
    setLoading(true); setErr("");
    const data = await lookupProperty(a, { includeOwner: false });
    setLoading(false);
    if (!data) { setManualMode(true); return; }
    onAddressSet(a, data);
  };

  return (
    <div className="s-intake" style={{ flex: 1 }}>
      <div className="s-intake-blob1" />
      <div className="s-intake-blob2" />
      <div className="s-intake-card">
        <div className="s-intake-pill">
          <span className="s-intake-dot" />
          <span>Seller client · {client.name}</span>
        </div>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(28px,5vw,44px)", fontWeight: 700, color: "#152d21", margin: "0 0 14px", lineHeight: 1.15 }}>
          Property address?<br /><em style={{ color: "#c47c1a", fontStyle: "italic" }}>We'll handle the rest.</em>
        </h1>
        <p className="lead" style={{ marginBottom: 28 }}>
          We'll pull beds, baths, sqft, and year built automatically — no paperwork needed.
        </p>
        <form onSubmit={submit} className="s-intake-form">
          <AddressAutocomplete
            value={address}
            onChange={setAddress}
            onPick={(f) => setAddress(f.place_name)}
            types="address"
            placeholder="123 Main St, San Francisco, CA"
            className="s-aac-wrap"
            inputClassName="s-intake-input"
            disabled={loading}
          />
          <button type="submit" className="s-intake-btn" disabled={loading || address.trim().length < 8}>
            {loading ? "Looking up…" : "Start journey →"}
          </button>
        </form>
        {err && <div className="s-intake-err">{err}</div>}

        {manualMode && (
          <div style={{ marginTop: 24, background: "#f7faf8", border: "1px solid #d8ede6", borderRadius: 14, padding: "20px 22px", textAlign: "left" }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#c47c1a", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
              Couldn't auto-pull property data
            </div>
            <p style={{ fontSize: 13, color: "#5b7365", lineHeight: 1.6, marginBottom: 16 }}>
              Enter the property details manually and the journey will continue.
            </p>
            <form onSubmit={submitManual}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                {[["beds", "Bedrooms", "3"], ["baths", "Bathrooms", "2"], ["sqft", "Sqft", "1640"], ["year_built", "Year built", "1985"]].map(([k, label, ph]) => (
                  <label key={k} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "#5b7365", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</span>
                    <input type="number" value={manual[k]} onChange={(e) => setM(k, e.target.value)} placeholder={ph}
                      style={{ background: "#fff", border: "1px solid #d8ede6", borderRadius: 8, padding: "9px 12px", fontSize: 14, color: "#1c2b25", fontFamily: "'Outfit',sans-serif" }} />
                  </label>
                ))}
              </div>
              <button type="submit" className="s-intake-btn" style={{ width: "100%", marginTop: 4 }}>
                Continue →
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Seller: journey carousel ─────────────────────────────────────────────────
function SellerJourney({ client, onUpdate }) {
  const [liveStages, setLiveStages] = useState(null);
  const [liveIdx, setLiveIdx]       = useState(null);

  // Load the seller's real journey from Supabase if they're linked.
  useEffect(() => {
    if (!supabase || !client.supabase_user_id) return;
    supabase
      .from("seller_listings")
      .select("stages, current_stage_idx, property, verification")
      .eq("client_id", client.supabase_user_id)
      .order("updated_at", { ascending: false })
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) {
          setLiveStages(data.stages || {});
          setLiveIdx(data.current_stage_idx ?? null);
        }
      });
  }, [client.supabase_user_id]); // eslint-disable-line

  const currentIdx   = liveIdx ?? client.currentStageIdx ?? 0;
  const currentStage = STAGES[currentIdx];
  const stages       = liveStages ?? client.stages ?? {};

  const journey = {
    id: client.id,
    address: client.address,
    property: client.property,
    currentStageIdx: currentIdx,
    stages,
    broker_id: client.broker_id || null,
    verification: { owner_of_record: true, is_self: false, relationship: "broker", confirmed_at: client.created_at },
    created_at: client.created_at,
    last_opened_at: new Date().toISOString(),
    sessionId: null,
  };

  const currentState = getStageState(journey, currentStage.id);
  const StageBody = currentStage.Component;

  const updateStage = (stageId, patch) => {
    const newStages = { ...stages, [stageId]: { ...(getStageState(journey, stageId)), ...patch } };
    onUpdate({ stages: newStages });
  };

  const goTo = (idx) => {
    const clamped = Math.max(0, Math.min(STAGE_COUNT - 1, idx));
    onUpdate({ currentStageIdx: clamped });
    track("broker_stage_view", { stage: STAGES[clamped].id, client_id: client.id });
  };

  const advance = () => {
    const cur = STAGES[currentIdx];
    const curState = getStageState(journey, cur.id);
    const newStages = { ...stages };
    if (curState.status !== "done") {
      newStages[cur.id] = { ...curState, status: "done" };
    }
    const nextIdx = Math.min(STAGE_COUNT - 1, currentIdx + 1);
    onUpdate({ currentStageIdx: nextIdx, stages: newStages });
    track("broker_stage_advanced", { stage: cur.id, client_id: client.id });
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      {liveStages && (
        <div style={{ padding: "4px 16px", background: "#f2f8f5", fontSize: 10, fontFamily: mono, color: "#7ecfb0", letterSpacing: "0.06em", textAlign: "right", borderBottom: "1px solid #e8f3ee" }}>
          ● live — synced from seller
        </div>
      )}
      {!liveStages && client.supabase_user_id && (
        <div style={{ padding: "4px 16px", background: "#fff8e6", fontSize: 10, fontFamily: mono, color: "#c47c1a", letterSpacing: "0.06em", textAlign: "right", borderBottom: "1px solid #f5e8c0" }}>
          seller hasn't synced yet — showing broker copy
        </div>
      )}
      <Stepper journey={journey} currentIdx={currentIdx} onJump={goTo} />
      <div style={{ flex: 1, overflowY: "auto" }}>
        <StageShell
          stage={currentStage}
          status={currentState.status}
          currentIdx={currentIdx}
          total={STAGE_COUNT}
          onBack={() => goTo(currentIdx - 1)}
          onAdvance={advance}
        >
          <StageBody
            state={currentState}
            journey={journey}
            update={(patch) => updateStage(currentStage.id, patch)}
          />
        </StageShell>
      </div>
    </div>
  );
}

// ─── Buyer: search + AI valuation ────────────────────────────────────────────
function BuyerPanel({ client, onUpdate }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [valBusy, setValBusy] = useState(false);
  const [filters, setFilters] = useState({
    location: client.location || "",
    price_min: client.price_min ? String(client.price_min) : "",
    price_max: client.price_max ? String(client.price_max) : "",
    beds_min: client.beds_min || "",
  });

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const search = async () => {
    setLoading(true); setListings([]); setSelectedId(null); setValuation(null);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    try {
      const res = await fetch(`/api/listings?${params}`, { headers: { "x-beta-token": BETA_SECRET } });
      const data = await res.json();
      setListings(data.rows || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { if (filters.location) search(); }, []); // eslint-disable-line

  const selected = listings.find((l) => l.id === selectedId) || null;

  useEffect(() => {
    if (!selected) { setValuation(null); return; }
    setValBusy(true); setValuation(null);
    const facts = [
      selected.beds != null && `${selected.beds} beds`,
      selected.baths != null && `${selected.baths} baths`,
      selected.sqft && `${selected.sqft.toLocaleString()} sqft`,
      selected.year_built && `built ${selected.year_built}`,
    ].filter(Boolean).join(", ");
    callAI({
      system: "You are a real estate valuation expert. Given property details, pull recent sold comps and return a fair market value range and recommended offer price. Be direct. Numbers only.",
      prompt: `Property: ${selected.address}, ${selected.city}, ${selected.state}\nDetails: ${facts}\nListed at: ${fmtPrice(selected.price)}\n\nProvide fair-price range, recommended offer, and 2-3 supporting comps.`,
      maxTokens: 600,
      cacheKey: `valuation:${normalizeAddressKey(`${selected.address} ${selected.city} ${selected.state}`)}`,
      cacheType: "valuation",
    }).then(({ text }) => { setValuation(text); setValBusy(false); }).catch(() => setValBusy(false));
  }, [selectedId]); // eslint-disable-line

  const inputStyle = { background: "#f7faf8", border: "1.5px solid #d8ede6", color: "#1c2b25", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontFamily: sans };

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
      {/* Listing list */}
      <div style={{ width: 320, borderRight: "1px solid #d8ede6", overflowY: "auto", padding: 16, flexShrink: 0 }}>
        {/* Search bar */}
        <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <input value={filters.location} onChange={(e) => setF("location", e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="City, ZIP…" style={{ ...inputStyle, width: "100%" }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            <input type="number" value={filters.price_min} onChange={(e) => setF("price_min", e.target.value)} placeholder="Min $" style={inputStyle} />
            <input type="number" value={filters.price_max} onChange={(e) => setF("price_max", e.target.value)} placeholder="Max $" style={inputStyle} />
            <select value={filters.beds_min} onChange={(e) => setF("beds_min", e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">Beds</option>
              {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}+</option>)}
            </select>
          </div>
          <button onClick={search} disabled={loading} style={{ background: "#2d7a62", color: "#fff", border: "none", borderRadius: 8, padding: "9px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
            {loading ? "Searching…" : "Search"}
          </button>
        </div>

        {loading && <div style={{ textAlign: "center", color: "#9bbfb2", fontSize: 13, padding: 20 }}>Loading listings…</div>}
        {!loading && listings.length === 0 && <div style={{ textAlign: "center", color: "#9bbfb2", fontSize: 13, padding: 20 }}>No listings — try searching above.</div>}

        {listings.map((l) => (
          <div key={l.id} onClick={() => setSelectedId(l.id)}
            style={{ background: selectedId === l.id ? "#fff" : "#f7faf8", border: `1.5px solid ${selectedId === l.id ? "#2d7a62" : "#d8ede6"}`, borderRadius: 12, padding: "12px 14px", marginBottom: 10, cursor: "pointer", transition: "all 0.12s" }}>
            {l.photo && <img src={l.photo} alt={l.address} style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 8, marginBottom: 8 }} onError={(e) => e.target.style.display = "none"} />}
            <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: "#1c2b25", marginBottom: 3 }}>{fmtPrice(l.price)}</div>
            <div style={{ fontSize: 11, fontFamily: mono, color: "#5a8a78", marginBottom: 3 }}>
              {l.beds}bd · {l.baths}ba{l.sqft ? ` · ${l.sqft.toLocaleString()} sqft` : ""}
            </div>
            <div style={{ fontSize: 12, color: "#1c2b25" }}>{l.address}</div>
          </div>
        ))}
      </div>

      {/* Detail + AI valuation */}
      <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
        {!selected ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#9bbfb2" }}>
            <div style={{ fontFamily: serif, fontSize: 20, marginBottom: 10, color: "#5a8a78" }}>Select a listing</div>
            <p style={{ fontSize: 13, lineHeight: 1.65 }}>Pick a property from the list to see the AI fair-price estimate and draft an offer for {client.name}.</p>
          </div>
        ) : (
          <div style={{ maxWidth: 580, animation: "fadeIn 0.3s ease" }}>
            <div style={{ fontFamily: serif, fontSize: 26, fontWeight: 700, color: "#1c2b25", marginBottom: 6 }}>{fmtPrice(selected.price)}</div>
            <div style={{ fontSize: 13, fontFamily: mono, color: "#5a8a78", marginBottom: 4 }}>
              {selected.beds}bd · {selected.baths}ba{selected.sqft ? ` · ${selected.sqft.toLocaleString()} sqft` : ""}{selected.year_built ? ` · built ${selected.year_built}` : ""}
            </div>
            <div style={{ fontSize: 14, color: "#1c2b25", marginBottom: 24 }}>{selected.address}, {selected.city}, {selected.state}</div>

            {/* AI Valuation */}
            <div style={{ background: "#f7faf8", border: "1px solid #d8ede6", borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <div style={{ fontFamily: mono, fontSize: 10, color: "#2d7a62", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>★ AI · Fair-price estimate</div>
              {valBusy && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", color: "#5a8a78", fontSize: 13 }}>
                  <div style={{ width: 14, height: 14, border: "2px solid #d8ede6", borderTop: "2px solid #2d7a62", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  Pulling comps…
                </div>
              )}
              {valuation && !valBusy && (
                <div style={{ fontSize: 13, color: "#1c2b25", lineHeight: 1.75 }} dangerouslySetInnerHTML={{ __html: renderMD(valuation) }} />
              )}
            </div>

            {/* Offer draft note */}
            <div style={{ background: "#1c2b25", borderRadius: 14, padding: 20 }}>
              <div style={{ fontFamily: mono, fontSize: 10, color: "#7ecfb0", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>Submit offer for {client.name}</div>
              <p style={{ fontSize: 13, color: "#7aad98", lineHeight: 1.65, marginBottom: 14 }}>
                To submit an offer on behalf of {client.name}, use the full buyer flow — it handles pre-approval upload, contingencies, and sends confirmation to {client.email || "your client"}.
              </p>
              <button
                onClick={() => {
                  const params = new URLSearchParams({ autooffer: "1" });
                  window.open(`/buy?${params}`, "_blank");
                }}
                style={{ background: "#2d7a62", color: "#7ecfb0", border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans }}
              >
                Open offer form →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard (empty right panel) ───────────────────────────────────────────
function Dashboard({ onAddSeller, onAddBuyer }) {
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div style={{ fontFamily: serif, fontSize: 28, fontWeight: 700, color: "#1c2b25", marginBottom: 12, lineHeight: 1.2 }}>
          Manage your clients.<br />
          <em style={{ color: "#2d7a62", fontStyle: "italic" }}>All their deals, handled.</em>
        </div>
        <p style={{ fontSize: 14, color: "#5a8a78", lineHeight: 1.7, marginBottom: 32 }}>
          Add a seller client to manage their full listing journey — valuation, prep, disclosures, offers, and closing. Add a buyer client to search listings and run AI fair-price estimates on their behalf.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={onAddSeller} style={{ background: "#2d7a62", color: "#fff", border: "none", borderRadius: 12, padding: "13px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: sans }}>
            + Add seller client
          </button>
          <button onClick={onAddBuyer} style={{ background: "#f2f8f5", color: "#1c2b25", border: "1.5px solid #d8ede6", borderRadius: 12, padding: "13px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
            + Add buyer client
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Notification bell + panel ────────────────────────────────────────────────
function NotificationBell({ notifs, onNavigate, onMarkAllRead }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const unread = notifs.filter((n) => !n.read).length;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const timeAgo = (iso) => {
    const m = Math.round((Date.now() - new Date(iso)) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  };

  const typeColor = (type) => type === "seller" ? "#2d7a62" : "#3b6ea8";
  const typeBg    = (type) => type === "seller" ? "#e4f7ef" : "#e4eef7";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ position: "relative", background: open ? "#f2f8f5" : "none", border: "1px solid rgba(45,122,98,0.15)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#5a8a78" }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span style={{ position: "absolute", top: 4, right: 4, width: 16, height: 16, background: "#c0354a", color: "#fff", borderRadius: "50%", fontSize: 9, fontFamily: mono, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 340, background: "#fff", border: "1px solid rgba(45,122,98,0.15)", borderRadius: 16, boxShadow: "0 12px 40px rgba(28,43,37,0.14)", zIndex: 200, overflow: "hidden" }}>
          {/* Panel header */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f4f2", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontFamily: mono, fontSize: 10, color: "#5a8a78", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>
              Notifications {unread > 0 && `· ${unread} new`}
            </span>
            {unread > 0 && (
              <button onClick={onMarkAllRead} style={{ background: "none", border: "none", fontFamily: mono, fontSize: 10, color: "#9bbfb2", cursor: "pointer", letterSpacing: "0.06em" }}>
                mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {notifs.length === 0 && (
              <div style={{ padding: "32px 16px", textAlign: "center", color: "#9bbfb2", fontSize: 13 }}>No notifications yet</div>
            )}
            {notifs.map((n) => (
              <button
                key={n.id}
                onClick={() => { onNavigate(n); setOpen(false); }}
                style={{ width: "100%", textAlign: "left", background: n.read ? "#fff" : "#f7fdf9", border: "none", borderBottom: "1px solid #f0f4f2", padding: "12px 16px", cursor: "pointer", display: "block", transition: "background 0.1s" }}
                onMouseOver={(e) => e.currentTarget.style.background = "#f2f8f5"}
                onMouseOut={(e) => e.currentTarget.style.background = n.read ? "#fff" : "#f7fdf9"}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  {/* Type dot */}
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: n.read ? "#d8ede6" : typeColor(n.client_type), flexShrink: 0, marginTop: 5 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1c2b25" }}>{n.title}</span>
                      <span style={{ fontSize: 9, fontFamily: mono, background: typeBg(n.client_type), color: typeColor(n.client_type), padding: "1px 6px", borderRadius: 6, letterSpacing: "0.06em" }}>
                        {n.client_type?.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#5a8a78", marginBottom: 2 }}>{n.client_name}</div>
                    <div style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2", letterSpacing: "0.04em" }}>
                      {timeAgo(n.created_at)} · tap to open →
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Timeline panel ───────────────────────────────────────────────────────────
// Days each stage takes on the critical path.
// Stages that run in parallel (disclosures & staging alongside prep;
// openhouse alongside showings) are marked 0 so they don't add extra time.
const STAGE_TYPICAL_DAYS = {
  valuation:   1,
  prep:       14,
  disclosures: 0,  // parallel with prep
  staging:     0,  // parallel with prep
  photography: 3,
  listing:     2,
  mls:         2,
  showings:   14,
  openhouse:   0,  // parallel with showings
  offers:      7,
  escrow:     30,
  closing:     1,
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const addDays = (dateStr, n) => {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const diffDays = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso + "T12:00:00") - new Date()) / 86400000);
};

// Compute the estimated close date from pending stages.
// Returns { estimatedCloseDate, remainingDays, stageRows }
function computeTimeline(stages) {
  let cursor = todayIso();
  const rows = STAGES.map((s) => {
    const status = stages?.[s.id]?.status || "pending";
    const days   = STAGE_TYPICAL_DAYS[s.id] ?? 0;
    const isDone = status === "done";
    const isCurrent = status === "in_progress";
    const estDate = isDone ? null : cursor;
    if (!isDone && days > 0) cursor = addDays(cursor, days);
    return { id: s.id, label: s.title, short: s.short, status, estDate, days };
  });
  return { estimatedCloseDate: cursor, stageRows: rows };
}

function TimelinePanel({ client, onUpdate, brokerId }) {
  const tl     = client.timeline || {};
  const set    = (k, v) => onUpdate({ timeline: { ...tl, [k]: v } });
  const stages = client.stages || {};
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (!supabase || !brokerId || !client.supabase_user_id) return;
    supabase.from("messages")
      .select("id, body, created_at")
      .eq("broker_id", brokerId)
      .eq("client_id", client.supabase_user_id)
      .eq("message_type", "disclosure")
      .order("created_at", { ascending: false })
      .then(({ data }) => setDocs(data || []));
  }, [brokerId, client.supabase_user_id]); // eslint-disable-line

  const { estimatedCloseDate, stageRows } = computeTimeline(stages);
  const displayClose = tl.close_date || estimatedCloseDate;
  const daysToClose  = diffDays(displayClose);
  const isOverridden = !!tl.close_date;

  const pendingCount = stageRows.filter((r) => r.status !== "done").length;
  const doneCount    = stageRows.filter((r) => r.status === "done").length;

  const bannerBg = daysToClose > 30 ? "#f2f8f5" : daysToClose > 7 ? "#fff8e6" : daysToClose >= 0 ? "#ffe6e6" : "#e6f7ef";

  const fieldStyle = { width: "100%", background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "9px 11px", fontSize: 13, fontFamily: sans, color: "#1c2b25" };
  const labelStyle = { display: "block", fontSize: 10, fontFamily: mono, color: "#5a8a78", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 };

  return (
    <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Documents */}
      {docs.length > 0 && (
        <div>
          <div style={{ fontFamily: mono, fontSize: 10, color: "#9bbfb2", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Documents</div>
          {docs.map((d) => {
            let doc = {};
            try { doc = JSON.parse(d.body); } catch {}
            return (
              <div key={d.id} style={{ border: "1.5px solid #2d7a62", borderRadius: 12, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ background: "#f2f8f5", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 18 }}>📋</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1c2b25", fontFamily: sans }}>Disclosure Docket</div>
                    <div style={{ fontSize: 10, fontFamily: mono, color: "#5a8a78", letterSpacing: "0.04em" }}>{doc.address}</div>
                  </div>
                  <button
                    onClick={() => openDisclosurePDF(doc.address, doc.questions, doc.answers, doc.summary)}
                    style={{ background: "#2d7a62", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: sans }}
                  >
                    View / Print →
                  </button>
                </div>
                <div style={{ padding: "8px 14px", background: "#fff", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  {["TDS", "SPQ", "NHD", "LEAD", "HOA"].map((f) => (
                    <span key={f} style={{ fontSize: 10, fontFamily: mono, background: "#f2f8f5", color: "#2d7a62", padding: "2px 7px", borderRadius: 4, letterSpacing: "0.06em" }}>{f}</span>
                  ))}
                  <span style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2", marginLeft: "auto" }}>
                    {doc.generated_at ? new Date(doc.generated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Countdown banner */}
      <div style={{ borderRadius: 12, padding: "16px 18px", textAlign: "center", background: bannerBg }}>
        {daysToClose > 0 && (
          <>
            <div style={{ fontFamily: mono, fontSize: 32, fontWeight: 700, color: "#1c2b25", lineHeight: 1 }}>{daysToClose}</div>
            <div style={{ fontFamily: mono, fontSize: 11, color: "#5a8a78", letterSpacing: "0.1em", marginTop: 4 }}>
              DAYS TO CLOSE · {fmtDate(displayClose)}
            </div>
            <div style={{ fontFamily: mono, fontSize: 10, color: "#9bbfb2", marginTop: 6, letterSpacing: "0.06em" }}>
              {pendingCount} stage{pendingCount !== 1 ? "s" : ""} pending · {doneCount} done
              {!isOverridden && " · estimated from progress"}
            </div>
          </>
        )}
        {daysToClose === 0 && <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: "#2d7a62" }}>Closing today!</div>}
        {daysToClose < 0 && (
          <>
            <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: "#2d7a62" }}>Closed {-daysToClose} days ago</div>
            <div style={{ fontFamily: mono, fontSize: 11, color: "#5a8a78", marginTop: 4 }}>{fmtDate(displayClose)}</div>
          </>
        )}
      </div>

      {/* Override close date */}
      <div>
        <label style={labelStyle}>Override close date <span style={{ color: "#9bbfb2", fontSize: 9 }}>(leave blank to auto-estimate)</span></label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="date" value={tl.close_date || ""} onChange={(e) => set("close_date", e.target.value)} style={fieldStyle} />
          {tl.close_date && (
            <button onClick={() => set("close_date", "")} style={{ background: "none", border: "1px solid #d8ede6", borderRadius: 8, padding: "9px 12px", fontSize: 11, fontFamily: mono, color: "#9bbfb2", cursor: "pointer" }}>
              clear
            </button>
          )}
        </div>
        {!isOverridden && (
          <div style={{ fontFamily: mono, fontSize: 10, color: "#9bbfb2", marginTop: 5, letterSpacing: "0.04em" }}>
            Auto: {fmtDate(estimatedCloseDate)} based on {pendingCount} pending stage{pendingCount !== 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Stage-by-stage timeline */}
      <div>
        <div style={{ fontFamily: mono, fontSize: 10, color: "#9bbfb2", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Stage progress</div>
        {stageRows.map((s, i) => {
          const isDone    = s.status === "done";
          const isCurrent = s.status === "in_progress";
          const isPending = s.status === "pending";
          const dotColor  = isDone ? "#2d7a62" : isCurrent ? "#e6a817" : "#d8ede6";
          const lineColor = isDone ? "#7ecfb0" : "#d8ede6";
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, paddingBottom: 14, position: "relative" }}>
              {i < stageRows.length - 1 && (
                <div style={{ position: "absolute", left: 9, top: 20, width: 2, height: "calc(100% - 6px)", background: lineColor }} />
              )}
              <div style={{
                width: 20, height: 20, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                background: dotColor, border: `2px solid ${dotColor}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {isDone    && <span style={{ color: "#fff", fontSize: 9, lineHeight: 1, fontWeight: 700 }}>✓</span>}
                {isCurrent && <span style={{ color: "#fff", fontSize: 9, lineHeight: 1, fontWeight: 700 }}>→</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: isCurrent ? 700 : 500, color: isDone ? "#9bbfb2" : "#1c2b25", fontFamily: sans, textDecoration: isDone ? "line-through" : "none" }}>
                  {s.label}
                </div>
                {!isDone && s.estDate && (
                  <div style={{ fontFamily: mono, fontSize: 11, color: isCurrent ? "#c47c1a" : "#5a8a78", letterSpacing: "0.04em", marginTop: 1 }}>
                    {isCurrent ? "in progress" : `est. ${fmtDate(s.estDate)}`}
                    {s.days > 0 && isPending && <span style={{ color: "#9bbfb2", marginLeft: 6 }}>~{s.days}d</span>}
                  </div>
                )}
                {isDone && <div style={{ fontFamily: mono, fontSize: 10, color: "#b8d8ce", letterSpacing: "0.04em", marginTop: 1 }}>complete</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Invite prompt (no supabase_user_id yet) ─────────────────────────────────
function InvitePrompt({ client, brokerId }) {
  const [copied, setCopied] = useState(false);
  const base    = client.type === "buyer" ? "/buy" : "/sell/v2";
  const link    = `${window.location.origin}${base}?broker=${brokerId}${client.email ? `&email=${encodeURIComponent(client.email)}` : ""}`;

  const copy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  return (
    <div style={{ padding: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center" }}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#f2f8f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>✉</div>
      <div style={{ fontFamily: serif, fontSize: 17, fontWeight: 700, color: "#1c2b25" }}>Invite {client.name}</div>
      <p style={{ fontSize: 13, color: "#5a8a78", lineHeight: 1.7, maxWidth: 300 }}>
        Send them this link. When they sign in, messaging unlocks here automatically.
      </p>
      <div style={{ background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 10, padding: "10px 14px", fontFamily: mono, fontSize: 11, color: "#5a8a78", wordBreak: "break-all", maxWidth: 340, lineHeight: 1.6 }}>
        {link}
      </div>
      <button onClick={copy} style={{ background: copied ? "#2d7a62" : "#1c2b25", color: "#fff", border: "none", borderRadius: 10, padding: "11px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: sans, transition: "background 0.15s" }}>
        {copied ? "✓ Copied!" : "Copy invite link"}
      </button>
      {client.email && (
        <a href={`mailto:${client.email}?subject=Your%206zero.ai%20portal&body=Hi%20${encodeURIComponent(client.name || "")}%2C%0A%0AHere%27s%20your%20link%20to%20access%20the%206zero.ai%20portal%3A%0A%0A${encodeURIComponent(link)}%0A%0AClick%20it%2C%20enter%20your%20email%2C%20and%20you%27ll%20get%20a%20magic%20link%20to%20sign%20in.`}
          style={{ fontFamily: mono, fontSize: 11, color: "#9bbfb2", letterSpacing: "0.06em", textDecoration: "none" }}>
          or send via email →
        </a>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function BrokerApp() {
  if (typeof window !== "undefined" && localStorage.getItem(BETA_KEY) !== BETA_SECRET) {
    window.location.href = "/public";
    return null;
  }

  const auth = useAuth();
  const brokerName  = auth?.profile?.name  || auth?.user?.email || null;
  const brokerEmail = auth?.user?.email || null;

  const [clients, setClients] = useState(loadClients);
  const [activeId, setActiveId] = useState(null);
  const [showAdd, setShowAdd] = useState(null); // null | "seller" | "buyer" | "any"
  const [addPrefill, setAddPrefill] = useState(null);
  const [notifs, setNotifs]   = useState(loadNotifs);
  const [view, setView]         = useState("clients");   // "clients" | "calendar" | "requests"
  const [panelTab, setPanelTab] = useState("journey");   // "journey" | "messages" | "timeline"

  useEffect(() => { saveClients(clients); }, [clients]);
  useEffect(() => { saveNotifs(notifs); }, [notifs]);
  useEffect(() => { setPanelTab("journey"); }, [activeId]);

  // Match Supabase user IDs to local clients by email via broker_client_links.
  useEffect(() => {
    if (!supabase || !auth?.user?.id) return;
    const resolve = async () => {
      const { data: links } = await supabase
        .from("broker_client_links")
        .select("client_id")
        .eq("broker_id", auth.user.id);
      if (!links?.length) return;
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, email")
        .in("id", links.map((l) => l.client_id));
      if (!profs?.length) return;
      setClients((prev) => {
        let changed = false;
        const next = prev.map((c) => {
          if (c.supabase_user_id) return c;
          const match = profs.find((p) => p.email?.toLowerCase() === c.email?.toLowerCase());
          if (match) { changed = true; return { ...c, supabase_user_id: match.id }; }
          return c;
        });
        return changed ? next : prev;
      });
    };
    resolve();
  }, [auth?.user?.id]); // eslint-disable-line

  // Mirror clients in a ref so the realtime callback sees current state
  const clientsRef = useRef(clients);
  useEffect(() => { clientsRef.current = clients; }, [clients]);

  // Subscribe to action-item completions across all broker threads
  useEffect(() => {
    if (!supabase || !auth?.user?.id) return;
    const brokerId = auth.user.id;
    const channel = supabase
      .channel(`completions:${brokerId}`)
      .on("postgres_changes", {
        event: "UPDATE", schema: "public", table: "messages",
        filter: `broker_id=eq.${brokerId}`,
      }, (payload) => {
        if (payload.new.completed_at && !payload.old?.completed_at) {
          const c = clientsRef.current.find((cl) => cl.supabase_user_id === payload.new.client_id);
          pushNotif({
            client_id:   c?.id || payload.new.client_id,
            client_name: c?.name || "Client",
            client_type: c?.type || "seller",
            stage_id: null, stage_idx: null,
            title: `✓ ${payload.new.action_title || "Action item"} completed`,
          });
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [auth?.user?.id]); // eslint-disable-line

  const pushNotif = (fields) => setNotifs((prev) => [mkNotif(fields), ...prev]);

  const mutateClient = (id, patch) => {
    if (id.startsWith("sample-")) return; // sample clients are read-only preview data
    setClients((prev) => {
      const old = prev.find((c) => c.id === id);
      // Auto-notify when a seller stage flips to "done".
      if (old && patch.stages) {
        Object.entries(patch.stages).forEach(([stageId, newState]) => {
          if (newState.status === "done" && old.stages?.[stageId]?.status !== "done") {
            const stageIdx = STAGES.findIndex((s) => s.id === stageId);
            pushNotif({
              client_id:   id,
              client_name: old.name,
              client_type: old.type,
              stage_id:    stageId,
              stage_idx:   stageIdx,
              title:       `${STAGE_LABELS[stageId] || stageId} complete`,
            });
          }
        });
      }
      return updateClient(prev, id, patch);
    });
  };

  const navigateToNotification = (notif) => {
    setActiveId(notif.client_id);
    if (notif.stage_idx != null) {
      // Jump the client to the notified stage.
      setClients((prev) => updateClient(prev, notif.client_id, { currentStageIdx: notif.stage_idx }));
    }
    setNotifs((prev) => prev.map((n) => n.id === notif.id ? { ...n, read: true } : n));
  };

  const markAllRead = () => setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));

  const addClient = (client) => {
    setClients((prev) => [client, ...prev]);
    setActiveId(client.id);
    track("broker_client_added", { type: client.type });
  };

  const deleteClient = (id) => {
    if (id.startsWith("sample-")) return; // nothing to delete, it's preview data
    if (!window.confirm("Remove this client? All their data will be deleted.")) return;
    setClients((prev) => prev.filter((c) => c.id !== id));
    if (activeId === id) setActiveId(null);
  };

  // Real clients are the source of truth (persisted); when there are none
  // at all, show the read-only sample pair instead of an empty portal —
  // this is the thing a broker trying the product for the first time
  // actually sees, not a blank "No clients yet."
  const displayClients = clients.length > 0 ? clients : SAMPLE_CLIENTS;
  const active = displayClients.find((c) => c.id === activeId) || null;

  const sellers = displayClients.filter((c) => c.type === "seller");
  const buyers  = displayClients.filter((c) => c.type === "buyer");

  const renderRightPanel = () => {
    if (!active) return <Dashboard onAddSeller={() => setShowAdd("seller")} onAddBuyer={() => setShowAdd("buyer")} />;

    if (active.type === "seller") {
      if (!active.address) {
        return (
          <SellerAddressEntry
            client={active}
            onAddressSet={(address, property) => mutateClient(active.id, { address, property })}
          />
        );
      }
      return (
        <SellerJourney
          client={active}
          onUpdate={(patch) => mutateClient(active.id, patch)}
        />
      );
    }

    if (active.type === "buyer") {
      return <BuyerPanel client={active} onUpdate={(patch) => mutateClient(active.id, patch)} />;
    }
  };

  const sectionLabel = (text) => (
    <div style={{ fontSize: 10, fontFamily: mono, color: "#9bbfb2", letterSpacing: "0.12em", textTransform: "uppercase", padding: "10px 14px 4px" }}>{text}</div>
  );

  return (
    <>
      <style>{BROKER_CSS}</style>
      <style>{SELLER_CSS}</style>
      {showAdd && (
        <AddClientModal
          defaultType={showAdd === "any" ? "seller" : showAdd}
          onAdd={addClient}
          onClose={() => { setShowAdd(null); setAddPrefill(null); }}
          brokerId={auth?.user?.id}
          brokerName={brokerName}
          prefill={addPrefill}
        />
      )}

      <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#f2f8f5" }}>

        {/* Header */}
        <header style={{ background: "#fff", borderBottom: "1px solid rgba(45,122,98,0.15)", padding: "10px 20px", display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          <Logo />
          <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, color: "#2b8259", letterSpacing: "0.2em" }}>6zero.ai</span>
          <span style={{ fontFamily: mono, fontSize: 10, color: "#9bbfb2", letterSpacing: "0.12em" }}>BROKER</span>
          <div style={{ flex: 1 }} />
          {/* View toggle */}
          <div style={{ display: "flex", background: "#f2f8f5", borderRadius: 8, padding: 3, border: "1px solid #d8ede6" }}>
            {[["clients", "clients"], ["calendar", "workboard"], ["requests", "requests"]].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)} style={{
                background: view === v ? "#fff" : "none", border: "none", borderRadius: 6,
                padding: "5px 12px", fontSize: 11, fontFamily: mono, letterSpacing: "0.06em",
                color: view === v ? "#1c2b25" : "#9bbfb2", cursor: "pointer", fontWeight: view === v ? 700 : 400,
                boxShadow: view === v ? "0 1px 4px rgba(0,0,0,0.08)" : "none", transition: "all 0.1s",
              }}>
                {label}
              </button>
            ))}
          </div>
          {brokerName && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#1c2b25", fontFamily: sans }}>{brokerName}</span>
              {brokerEmail && brokerEmail !== brokerName && (
                <span style={{ fontSize: 10, color: "#9bbfb2", fontFamily: mono, letterSpacing: "0.04em" }}>{brokerEmail}</span>
              )}
            </div>
          )}
          <NotificationBell
            notifs={notifs}
            onNavigate={navigateToNotification}
            onMarkAllRead={markAllRead}
          />
          <button onClick={() => setShowAdd("any")} style={{ background: "#2d7a62", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
            + Add client
          </button>
        </header>

        {/* Body */}
        <div style={{ flex: 1, display: "flex", minHeight: 0, flexDirection: view === "clients" ? "row" : "column" }}>
        {view === "calendar" && (
          <BrokerWorkboard
            clients={clients}
            onSelectClient={(id, tab) => { setView("clients"); setActiveId(id); if (tab) setPanelTab(tab); }}
          />
        )}
        {view === "requests" && (
          <BrokerRequestsPanel
            onConvertToClient={(r) => {
              setAddPrefill({ type: "buyer", name: r.name, email: r.email, phone: r.phone || "" });
              setShowAdd("buyer");
            }}
          />
        )}
        {view === "clients" && <div style={{ flex: 1, display: "flex", minHeight: 0 }}>

          {/* Sidebar */}
          <div style={{ width: 240, background: "#fff", borderRight: "1px solid rgba(45,122,98,0.12)", overflowY: "auto", flexShrink: 0, padding: "10px 8px" }}>

            {sellers.length > 0 && (
              <>
                {sectionLabel(`Sellers · ${sellers.length}`)}
                {sellers.map((c) => (
                  <ClientCard key={c.id} client={c} active={activeId === c.id} onClick={() => setActiveId(c.id)} />
                ))}
              </>
            )}

            {buyers.length > 0 && (
              <>
                {sectionLabel(`Buyers · ${buyers.length}`)}
                {buyers.map((c) => (
                  <ClientCard key={c.id} client={c} active={activeId === c.id} onClick={() => setActiveId(c.id)} />
                ))}
              </>
            )}

            {clients.length === 0 && (
              <div style={{ textAlign: "center", padding: "14px 14px 4px", color: "#9bbfb2" }}>
                <div style={{ fontSize: 11, fontFamily: mono, letterSpacing: "0.04em" }}>
                  ★ Viewing sample clients — add a real one below to get started
                </div>
              </div>
            )}

            <div style={{ padding: "16px 14px 8px", display: "flex", flexDirection: "column", gap: 6 }}>
              <button onClick={() => setShowAdd("seller")} style={{ background: "#f2f8f5", color: "#2d7a62", border: "1px solid #d8ede6", borderRadius: 8, padding: "8px", fontSize: 11, cursor: "pointer", fontFamily: sans, fontWeight: 600 }}>
                + Seller
              </button>
              <button onClick={() => setShowAdd("buyer")} style={{ background: "#f2f8f5", color: "#3b6ea8", border: "1px solid #d8ede6", borderRadius: 8, padding: "8px", fontSize: 11, cursor: "pointer", fontFamily: sans, fontWeight: 600 }}>
                + Buyer
              </button>
            </div>

            {/* Delete active client */}
            {active && !active.is_sample && (
              <div style={{ padding: "8px 14px" }}>
                <button onClick={() => deleteClient(active.id)} style={{ background: "none", border: "none", color: "#c0354a", fontSize: 11, cursor: "pointer", fontFamily: mono, letterSpacing: "0.06em" }}>
                  Remove client ×
                </button>
              </div>
            )}
          </div>

          {/* Right panel */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
            {active?.is_sample && (
              <div style={{ background: "#fef4e8", borderBottom: "1px solid #f1d6a8", padding: "8px 18px", fontSize: 11, fontFamily: mono, color: "#c47c1a", letterSpacing: "0.04em", flexShrink: 0 }}>
                ★ SAMPLE CLIENT — read-only preview, not a real person. Add your first real client to replace this.
              </div>
            )}
            {active && (
              <div className="s-top" style={{ padding: "9px 18px", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#1c2b25", fontFamily: sans }}>{active.name}</span>
                  {(active.email || active.phone) && (
                    <span style={{ fontSize: 11, color: "#9bbfb2", fontFamily: mono, letterSpacing: "0.04em" }}>
                      {[active.email, active.phone].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </div>
                {active.type === "seller" && active.address && (
                  <span className="s-addr-pill">{active.address}</span>
                )}
                {active.type === "buyer" && active.location && (
                  <span className="s-addr-pill">
                    {active.location}{active.price_max ? ` · up to ${fmtPrice(active.price_max)}` : ""}
                  </span>
                )}
              </div>
            )}
            {/* Tab bar — journey / timeline / messages */}
            {active && (
              <div style={{ display: "flex", borderBottom: "1px solid #d8ede6", background: "#fff", flexShrink: 0 }}>
                {[
                  ["journey",  active.type === "seller" ? "Seller journey" : "Buyer search"],
                  ...(active.type === "seller" ? [["timeline", "Timeline"]] : []),
                  ...(active.is_sample ? [] : [["messages", "Messages"]]),
                ].map(([tab, label]) => (
                  <button key={tab} onClick={() => setPanelTab(tab)} style={{
                    padding: "9px 18px", fontSize: 12, fontWeight: panelTab === tab ? 700 : 400,
                    color: panelTab === tab ? "#2d7a62" : "#9bbfb2", background: "none", border: "none",
                    borderBottom: panelTab === tab ? "2px solid #2d7a62" : "2px solid transparent",
                    cursor: "pointer", fontFamily: sans, letterSpacing: "0.04em", transition: "all 0.1s",
                  }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div className={active?.type === "seller" && panelTab === "journey" ? "br-seller-panel" : undefined}
              style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflowY: (active?.type === "buyer" && panelTab === "journey") ? "hidden" : "auto" }}>
              {panelTab === "timeline" && active ? (
                <TimelinePanel client={active} onUpdate={(patch) => mutateClient(active.id, patch)} brokerId={auth?.user?.id} />
              ) : panelTab === "messages" && active ? (
                !auth?.user?.id ? (
                  <div style={{ padding: 28, textAlign: "center", color: "#9bbfb2", fontFamily: mono, fontSize: 12, lineHeight: 1.8 }}>
                    Sign in with Supabase to enable messaging.
                  </div>
                ) : !active.supabase_user_id ? (
                  <InvitePrompt client={active} brokerId={auth.user.id} />
                ) : (
                  <MessageThread
                    brokerId={auth.user.id}
                    clientId={active.supabase_user_id}
                    myRole="broker"
                    clientName={active.name}
                  />
                )
              ) : (
                renderRightPanel()
              )}
            </div>
          </div>

        </div>}
      </div>
    </div>
    </>
  );
}
